import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError } from '@geefamily/api';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/features/auth/use-auth';
import { useAuthDialog } from '@/features/auth/use-auth-dialog';
import {
  useClaimMilestone,
  useMilestones,
  useMyMilestones,
  type Milestone,
  type MilestoneProgress,
  type MilestoneReceipt,
} from '../api';
import { clearClaim, readClaim, storeClaim } from '../claim-intent';
import { RoadmapPath } from '../components/RoadmapPath';
import { MilestoneWorld } from '../components/MilestoneWorld';
import type { IslandVisual } from '../scene/types';
import '../milestones.css';
const number = (value: number) =>
  new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(value);
export function MilestonesPage() {
  const { user } = useAuth();
  return <MilestoneSession key={user?.id ?? 'guest'} />;
}
function MilestoneSession() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { open } = useAuthDialog();
  const definitions = useMilestones();
  const progress = useMyMilestones();
  const roadmap = useRef<HTMLDivElement>(null);
  const submitting = useRef(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ id: string; text: string } | null>(null);
  const [receipts, setReceipts] = useState<Record<string, MilestoneReceipt>>({});
  const claim = useClaimMilestone((receipt) =>
    setReceipts((current) => ({ ...current, [receipt.claim.milestoneId]: receipt })),
  );
  const rows: { milestone: Milestone; progress: MilestoneProgress | null }[] = isAuthenticated
    ? (progress.data?.rows.map((row) => ({ milestone: row.milestone, progress: row })) ?? [])
    : (definitions.data?.map((milestone) => ({ milestone, progress: null })) ?? []);
  const visuals: IslandVisual[] = rows.map(({ milestone, progress: row }) => ({
    id: milestone.id,
    theme: milestone.theme,
    state:
      receipts[milestone.id] || row?.status === 'claimed'
        ? 'claimed'
        : row?.status === 'ready'
          ? 'ready'
          : 'locked',
    reveal: receipts[milestone.id]?.claim.id,
  }));
  const loading = isLoading || (isAuthenticated ? progress.isPending : definitions.isPending);
  const failed = isAuthenticated
    ? progress.isError && !progress.data
    : definitions.isError && !definitions.data;
  async function activate(milestone: Milestone, row: MilestoneProgress | null) {
    if (!isAuthenticated || !user) {
      open('login');
      return;
    }
    if (submitting.current || row?.status === 'claimed' || receipts[milestone.id]) return;
    const previous = readClaim(user.id, milestone.id);
    if (row?.status !== 'ready' && !previous) return;
    const body = previous ?? { requestId: crypto.randomUUID(), expectedVersion: milestone.version };
    try {
      storeClaim(user.id, milestone.id, body);
    } catch {
      setNotice({
        id: milestone.id,
        text: 'İşlemi güvenle saklayamadık. Tarayıcı oturum depolamasını etkinleştirip tekrar dene.',
      });
      return;
    }
    submitting.current = true;
    setBusyId(milestone.id);
    setNotice(null);
    try {
      const result = await claim.mutateAsync({ id: milestone.id, body });
      clearClaim(user.id, milestone.id);
      setReceipts((current) => ({ ...current, [milestone.id]: result }));
      setNotice({
        id: milestone.id,
        text: `${number(result.claim.rewardCoins)} GeeCoin hesabına eklendi.`,
      });
    } catch (error) {
      const uncertain =
        !(error instanceof ApiError) ||
        error.status >= 500 ||
        error.status === 408 ||
        error.status === 429;
      if (!uncertain) clearClaim(user.id, milestone.id);
      setNotice({
        id: milestone.id,
        text: uncertain
          ? 'Sonucu henüz doğrulayamadık. Aynı sandığa tekrar dokunarak işlemi kontrol edebilirsin.'
          : error instanceof ApiError
            ? error.message
            : 'Ödül alınamadı. Tekrar dene.',
      });
    } finally {
      submitting.current = false;
      setBusyId(null);
    }
  }
  return (
    <div className="milestone-page">
      <header className="milestone-hero">
        <div className="milestone-kicker">
          <span />
          GEEFAMILY · MILESTONE
        </div>
        <h1>
          Her adım bir hikâye.
          <br />
          <em>Sıradaki ada senin.</em>
        </h1>
        <p>
          Tamamlanan yarışmalardaki ilerlemeni keşfet. Hedefe ulaş, sandığını aç ve GeeCoin’lerini
          topla.
        </p>
        <a href="#milestone-roadmap" className="milestone-explore">
          Yolculuğu keşfet <span aria-hidden>↓</span>
        </a>
        <span className="milestone-hero-orbit" aria-hidden />
        <span className="milestone-coordinate" aria-hidden>
          THE GEEFAMILY ISLANDS
        </span>
      </header>
      <section className="milestone-summary" aria-label="Milestone özeti">
        {isLoading ? (
          <p role="status">Oturum kontrol ediliyor…</p>
        ) : !isAuthenticated ? (
          <>
            <div>
              <strong>Yolculuğun burada başlıyor.</strong>
              <p>İlerlemeni ve açabileceğin sandıkları görmek için giriş yap.</p>
            </div>
            <Button onClick={() => open('login')}>Giriş yap ↗</Button>
          </>
        ) : progress.data ? (
          <>
            <div>
              <span>Alınabilir sandık</span>
              <strong>{progress.data.claimableCount}</strong>
            </div>
            <div>
              <span>Açılan sandık</span>
              <strong>{progress.data.claimedCount}</strong>
            </div>
            <div>
              <span>GeeCoin bakiyen</span>
              <strong>
                {number(progress.data.account.balance)} <small>GC</small>
              </strong>
            </div>
            <Link to="/geecoin">Hesabına git ↗</Link>
          </>
        ) : (
          <p role="status">Milestone özeti {progress.isError ? 'yüklenemedi.' : 'yükleniyor…'}</p>
        )}
      </section>
      {isAuthenticated && progress.data?.linkedAccountCount === 0 ? (
        <div className="milestone-link-account">
          <div>
            <strong>Önce oyun hesabını ekle.</strong>
            <p>
              Partner kullanıcı adını veya oyuncu ID’ni profiline kaydet. Eşleşen geçmiş
              yarışmaların da ilerlemene dahil edilir.
            </p>
          </div>
          <Link to="/profile" className="gf-button gf-button-secondary">
            Hesabımı ekle ↗
          </Link>
        </div>
      ) : null}
      <div ref={roadmap} id="milestone-roadmap" className="milestone-roadmap">
        {loading ? (
          <div className="milestone-loading" role="status">
            <span />
            Adalar yükleniyor…
          </div>
        ) : failed ? (
          <EmptyState
            title="Yolculuğun yüklenemedi"
            description="İlerlemeni doğrulamak için tekrar deneyebilirsin."
          >
            <Button
              className="mt-5"
              onClick={() => {
                void (isAuthenticated ? progress.refetch() : definitions.refetch());
              }}
            >
              Tekrar dene
            </Button>
          </EmptyState>
        ) : rows.length === 0 ? (
          <EmptyState
            title="Yeni adalar hazırlanıyor"
            description="Milestone hedefleri yayınlandığında bu yolculukta yerlerini alacak."
          />
        ) : (
          <>
            <RoadmapPath roadmap={roadmap} count={rows.length} />
            <ol className="milestone-stops">
              {rows.map(({ milestone, progress: row }, index) => {
                const receipt = receipts[milestone.id]?.claim ?? row?.claim;
                const state = receipt ? 'claimed' : row?.status === 'ready' ? 'ready' : 'locked';
                const pending = user ? readClaim(user.id, milestone.id) : null;
                const actionable = !isAuthenticated || state === 'ready' || Boolean(pending);
                const label =
                  state === 'claimed'
                    ? 'Sandık açıldı'
                    : busyId === milestone.id
                      ? 'Kontrol ediliyor…'
                      : pending
                        ? 'İşlemi kontrol et'
                        : state === 'ready'
                          ? 'Sandığı aç'
                          : !isAuthenticated
                            ? 'Giriş yap ve keşfet'
                            : 'Sandık kilitli';
                return (
                  <li
                    key={milestone.id}
                    className={`milestone-stop milestone-${state} milestone-theme-${milestone.theme}`}
                  >
                    <div className="milestone-island-slot" data-island-id={milestone.id}>
                      <div className="milestone-island-fallback" aria-hidden>
                        <div className="milestone-fallback-chest">
                          <span />
                          <i />
                        </div>
                        <div className="milestone-fallback-land" />
                      </div>
                      <button
                        className="milestone-chest-hit"
                        aria-label={`${milestone.title}: ${label}`}
                        aria-describedby={`milestone-info-${milestone.id}`}
                        disabled={!actionable || Boolean(busyId)}
                        onClick={() => {
                          void activate(milestone, row);
                        }}
                      >
                        <span className="milestone-chest-label">
                          {label}
                          <span aria-hidden>
                            {state === 'claimed' ? ' ✓' : state === 'ready' ? ' ↗' : ''}
                          </span>
                        </span>
                      </button>
                      <span className="milestone-island-number" aria-hidden>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="milestone-stop-info" id={`milestone-info-${milestone.id}`}>
                      <div className="milestone-stop-meta">
                        <span>ADA {String(index + 1).padStart(2, '0')}</span>
                        <span>
                          {milestone.siteName ?? 'Tüm partnerler'} · {milestone.currency}
                        </span>
                      </div>
                      <h2>{milestone.title}</h2>
                      <p>{milestone.description}</p>
                      <div className="milestone-reward">
                        <span className="milestone-coin" aria-hidden>
                          G
                        </span>
                        <strong>+{number(receipt?.rewardCoins ?? milestone.rewardCoins)}</strong>
                        <span>GeeCoin</span>
                      </div>
                      <div className="milestone-progress-copy">
                        <span>
                          {row ? `${number(row.wagered)} / ` : ''}
                          {number(milestone.threshold)} {milestone.currency}
                        </span>
                        <span>{row ? `%${number(row.progressPercent)}` : 'Çevrim hedefi'}</span>
                      </div>
                      {row ? (
                        <div
                          className="milestone-progress-track"
                          role="progressbar"
                          aria-label={`${milestone.title} ilerlemesi`}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={row.progressPercent}
                        >
                          <span style={{ width: `${row.progressPercent}%` }} />
                        </div>
                      ) : null}
                      <p className="milestone-qualification">
                        {receipt
                          ? 'Ödül hesabına eklendi.'
                          : row
                            ? `${row.eligibleRaceCount} tamamlanmış yarışmadan hesaplandı.`
                            : 'Tamamlanmış yarışmalardaki toplam çevrim.'}
                      </p>
                      {notice?.id === milestone.id ? (
                        <p className="milestone-notice" role="status">
                          {notice.text}
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
            <MilestoneWorld roadmap={roadmap} items={visuals} />
          </>
        )}
      </div>
      <aside className="milestone-rules">
        <span aria-hidden>✦</span>
        <div>
          <h2>Her adımın kaydı var.</h2>
          <p>
            İlerleme, tamamlanmış yarışmaların son sıralamalarından hesaplanır. Para birimleri ayrı
            değerlendirilir. Bağladığın hesapla eşleşen geçmiş sonuçlar dahil edilir; her sandığın
            ödülü bir kez alınır.
          </p>
          <Link to="/profile">Bağlı oyun hesaplarını yönet ↗</Link>
        </div>
      </aside>
    </div>
  );
}
